import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useT } from '@/shared/i18n/useT';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { hideInfoModal, type InfoModal } from '@/shared/store/slices/uiSlice';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { feedbackMsgs } from './feedbackMsgs';

const TYPES = {
  error: { icon: CircleAlert, chip: 'bg-down-tint text-down' },
  warning: { icon: TriangleAlert, chip: 'bg-warn-tint text-warn' },
  success: { icon: CircleCheck, chip: 'bg-up-tint text-up' },
  info: { icon: Info, chip: 'bg-info-tint text-info' },
} as const satisfies Record<InfoModal['type'], { icon: LucideIcon; chip: string }>;

/**
 * Modal global de avisos (`ui.infoModal`): errores que bloquean, confirmaciones importantes, información.
 * Los textos pueden ser una clave `errors.*` (se traduce) o texto ya localizado. Conserva el último
 * contenido mientras dura la animación de salida para que no se vacíe al cerrar.
 */
export function DisplayMessage() {
  const { t } = useT(feedbackMsgs);
  const { t: tc, te } = useT();
  const dispatch = useAppDispatch();
  const infoModal = useAppSelector((s) => s.ui.infoModal);
  const [shown, setShown] = useState<InfoModal | null>(infoModal);
  if (infoModal && infoModal !== shown) setShown(infoModal);

  const type = shown?.type ?? 'info';
  const spec = TYPES[type];
  const close = () => dispatch(hideInfoModal());

  return (
    <Dialog
      open={infoModal !== null}
      onOpenChange={(open) => !open && close()}
      role={type === 'error' || type === 'warning' ? 'alertdialog' : 'dialog'}
      size="sm"
      title={shown ? te(shown.title ?? t(`modal.${type}`)) : ''}
      description={shown ? te(shown.description) : undefined}
      icon={
        <span className={cn('grid size-12 place-items-center rounded-full', spec.chip)}>
          <Icon icon={spec.icon} size={24} />
        </span>
      }
      footer={<Button onClick={close}>{tc('actions.accept')}</Button>}
    >
      {shown?.code !== undefined ? (
        <p className="num text-xs text-fg-muted">{t('modal.code', { code: String(shown.code) })}</p>
      ) : null}
    </Dialog>
  );
}
